/**
 * Utilitários de segurança para URLs e redirecionamentos
 * Previne vulnerabilidades de Open Redirect e injeção de URLs maliciosas
 */

// Função para validar e sanitizar códigos de acervo
const validateAndSanitizeCodAcervo = (cod) => {
    if (!cod) return null;
    
    // Remove caracteres especiais e mantém apenas alfanuméricos, hífens e underscores
    const sanitized = cod.toString().replace(/[^a-zA-Z0-9\-_]/g, '');
    
    // Valida se o código sanitizado não está vazio e tem um tamanho razoável
    if (sanitized.length === 0 || sanitized.length > 50) {
        return null;
    }
    
    return sanitized;
};

// Lista de caminhos permitidos para redirecionamento (whitelist)
const ALLOWED_PATHS = [
    '/acesso-restrito',
    '/acervo',
    '/login',
    '/pesquisa',
    '/biblioteca',
    '/renovacao',
    '/reserva',
    '/usuario',
    '/perfil',
    '/dashboard',
    '/admin',
    '/',
    '/catalogos'
];

/**
 * Constrói URLs seguras validando contra uma whitelist de caminhos permitidos
 * @param {string} path - O caminho da URL a ser validado
 * @param {string} codAcervo - Código do acervo (opcional) para validação adicional
 * @returns {string} - URL validada ou '/' como fallback seguro
 */
export const buildSecureUrl = (path, codAcervo = null) => {
    if (!path || typeof path !== 'string') {
        return '/';
    }

    // Extrai o caminho base da URL (sem query parameters)
    const basePath = path.split('?')[0];
    const pathSegments = basePath.split('/').filter(segment => segment);
    
    // Para URLs com parâmetros dinâmicos, verifica apenas o padrão base
    let normalizedPath = '/';
    if (pathSegments.length > 0) {
        normalizedPath = '/' + pathSegments[0];
        
        // Se há um segundo segmento, valida se é um código de acervo válido
        if (pathSegments.length > 1 && pathSegments[1]) {
            const isValidCod = validateAndSanitizeCodAcervo(pathSegments[1]);
            if (!isValidCod) {
                // Se o segundo segmento parece ser um código de acervo mas não é válido, redireciona para home
                return '/';
            }
        }
    }
    
    // Verifica se o caminho normalizado está na lista de permitidos
    const isAllowed = ALLOWED_PATHS.some(allowed => {
        if (allowed === '/') return normalizedPath === '/';
        return normalizedPath.startsWith(allowed);
    });
    
    if (!isAllowed) {
        return '/'; // Redireciona para home se não for um caminho permitido
    }
    
    // Valida o código do acervo se fornecido explicitamente
    if (codAcervo && !validateAndSanitizeCodAcervo(codAcervo)) {
        return '/';
    }
    
    return path;
};

const validateCodCategoria = (codCategoria) => {
    if (!codCategoria) return null;

    const sanitized = codCategoria.toString().replace(/[^0-9.]/g, '');

    if (!sanitized || sanitized.length > 50) {
        return null;
    }

    return sanitized;
};

/**
 * Executa navegação segura usando o hook navigate do React Router v7
 * @param {function} navigate - Função navigate do hook useNavigate
 * @param {string} path - Caminho para navegação
 * @param {string} codAcervo - Código do acervo (opcional)
 * @param {boolean} replace - Se true, usa replace, se false usa push
 */
export const safeNavigate = (navigate, path, codAcervo = null, replace = false) => {
    const secureUrl = buildSecureUrl(path, codAcervo);
    
    if (typeof navigate === 'function') {
        if (replace) {
            navigate(secureUrl, { replace: true });
        } else {
            navigate(secureUrl);
        }
    } else {
        // Fallback para home se a função navigate não existir
        window.location.href = '/';
    }
};

/**
 * Wrapper seguro para navigate que valida a URL antes do redirecionamento
 * @param {function} navigate - Função navigate do hook useNavigate
 * @param {string} path - Caminho para navegação
 * @param {string} codAcervo - Código do acervo (opcional)
 */
export const securePush = (navigate, path, codAcervo = null) => {
    safeNavigate(navigate, path, codAcervo, false);
};

/**
 * Wrapper seguro para navigate com replace que valida a URL antes do redirecionamento
 * @param {function} navigate - Função navigate do hook useNavigate
 * @param {string} path - Caminho para navegação
 * @param {string} codAcervo - Código do acervo (opcional)
 */
export const secureReplace = (navigate, path, codAcervo = null) => {
    safeNavigate(navigate, path, codAcervo, true);
};

/**
 * Valida se uma URL é segura para redirecionamento
 * @param {string} url - URL a ser validada
 * @returns {boolean} - true se a URL é segura, false caso contrário
 */
export const isSecureUrl = (url) => {
    const secureUrl = buildSecureUrl(url);
    return secureUrl !== '/';
};

export { validateAndSanitizeCodAcervo,validateCodCategoria };
